import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X, Globe } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Label } from '../../../components/ui/label';
import { CountryFlag, getCountryName } from './CountryPicker';
import regionsConfig from '../../../shared-config/regions.config.json';

// All location regions from UN M49 + business terms
export type LocationRegion =
  // UN M49 Macro Regions (Continents)
  | 'AFRICA'
  | 'AMERICAS'
  | 'ASIA'
  | 'EUROPE'
  | 'OCEANIA'
  // UN M49 Sub-Regions (Africa)
  | 'NORTHERN_AFRICA'
  | 'EASTERN_AFRICA'
  | 'MIDDLE_AFRICA'
  | 'SOUTHERN_AFRICA'
  | 'WESTERN_AFRICA'
  // UN M49 Sub-Regions (Americas)
  | 'CARIBBEAN'
  | 'CENTRAL_AMERICA'
  | 'SOUTH_AMERICA'
  | 'NORTHERN_AMERICA'
  // UN M49 Sub-Regions (Asia)
  | 'CENTRAL_ASIA'
  | 'EASTERN_ASIA'
  | 'SOUTHEASTERN_ASIA'
  | 'SOUTHERN_ASIA'
  | 'WESTERN_ASIA'
  // UN M49 Sub-Regions (Europe)
  | 'EASTERN_EUROPE'
  | 'NORTHERN_EUROPE'
  | 'SOUTHERN_EUROPE'
  | 'WESTERN_EUROPE'
  // UN M49 Sub-Regions (Oceania)
  | 'AUSTRALIA_NEW_ZEALAND'
  | 'MELANESIA'
  | 'MICRONESIA'
  | 'POLYNESIA'
  // UN M49 Intermediate Regions
  | 'LATIN_AMERICA_CARIBBEAN'
  | 'SUB_SAHARAN_AFRICA'
  // Business-Friendly Regions
  | 'EMEA'
  | 'APAC'
  | 'MENA'
  | 'ANZ'
  | 'DACH'
  | 'BENELUX'
  | 'NORDICS'
  | 'BALTICS'
  | 'GCC'
  | 'ASEAN'
  | 'LATAM'
  // Special
  | 'GLOBAL';

export interface RegionOption {
  code: LocationRegion;
  name: string;
  description: string;
  countryCount: number;
  countries: string[];
  type: 'un_macro' | 'un_sub' | 'un_intermediate' | 'business' | 'special';
}

// Parse regions from config, filtering out _comment entries
const regionOptions: RegionOption[] = Object.entries(regionsConfig.regions)
  .filter(([code]) => !code.startsWith('_comment'))
  .map(([code, data]) => {
    const countries = ((data as { countries?: string[] }).countries || []);
    return {
      code: code as LocationRegion,
      name: (data as { name: string }).name,
      description: (data as { description: string }).description,
      countryCount: countries.length,
      countries,
      type: ((data as { type?: string }).type || 'special') as RegionOption['type'],
    };
  });

/** Render country flags content for a region - shows all countries with flags and names */
function RegionCountriesTooltipContent({ region }: { region: RegionOption }) {
  const { t } = useTranslation('common');

  if (region.countries.length === 0) return null;

  return (
    <div className="w-96">
      <p className="text-sm font-medium mb-1">{region.name}</p>
      <p className="text-xs text-muted-foreground mb-3">
        {t('pickers.countryCount', { count: region.countries.length })}
      </p>
      <div className="max-h-72 overflow-y-auto pr-1">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {region.countries.map((code) => (
            <div key={code} className="flex items-center gap-2 min-w-0">
              <span className="shrink-0">
                <CountryFlag code={code} size={16} />
              </span>
              <span className="text-xs truncate" title={getCountryName(code)}>
                {getCountryName(code)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Wrapper component exported for use in tooltips */
export function RegionCountriesTooltip({ region }: { region: RegionOption }) {
  return <RegionCountriesTooltipContent region={region} />;
}

// Group regions by type for better organization in the dropdown
const regionsByType = {
  business: regionOptions.filter((r) => r.type === 'business'),
  un_macro: regionOptions.filter((r) => r.type === 'un_macro'),
  un_sub: regionOptions.filter((r) => r.type === 'un_sub'),
  un_intermediate: regionOptions.filter((r) => r.type === 'un_intermediate'),
  special: regionOptions.filter((r) => r.type === 'special'),
};

interface MultiRegionPickerProps {
  value: LocationRegion[];
  onChange: (regions: LocationRegion[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  /** Maximum tags to display before showing "+N more" */
  maxDisplayTags?: number;
  className?: string;
  /** Whether to show the GLOBAL option (default: false) */
  showGlobal?: boolean;
}

export function MultiRegionPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  maxDisplayTags = 3,
  className,
  showGlobal = false,
}: MultiRegionPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const filteredOptions = useMemo(
    () => showGlobal ? regionOptions : regionOptions.filter((r) => r.code !== 'GLOBAL'),
    [showGlobal]
  );

  const selectedRegions = useMemo(
    () => filteredOptions.filter((region) => value.includes(region.code)),
    [filteredOptions, value]
  );

  const handleSelect = (code: LocationRegion) => {
    if (value.includes(code)) {
      onChange(value.filter((v) => v !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const handleRemove = (code: LocationRegion, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== code));
  };

  const displayedRegions = selectedRegions.slice(0, maxDisplayTags);
  const remainingCount = selectedRegions.length - maxDisplayTags;

  // Filter grouped regions based on showGlobal
  const businessRegions = regionsByType.business;
  const macroRegions = regionsByType.un_macro;
  const subRegions = regionsByType.un_sub;
  const intermediateRegions = regionsByType.un_intermediate;

  const renderRegionItem = (region: RegionOption) => (
    <CommandItem
      key={region.code}
      value={`${region.name} ${region.description}`}
      onSelect={() => handleSelect(region.code)}
    >
      <Check
        className={cn(
          'me-2 h-4 w-4',
          value.includes(region.code) ? 'opacity-100' : 'opacity-0'
        )}
      />
      <Globe className="me-2 h-4 w-4 text-muted-foreground" />
      <div className="flex-1">
        <p className="text-sm">{region.name}</p>
        <p className="text-xs text-muted-foreground">
          {region.description}
          {region.countryCount > 0 && ` (${region.countryCount} countries)`}
        </p>
      </div>
    </CommandItem>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn(fullWidth && 'w-full', className)}>
        {label && (
          <Label className="mb-1.5 block">
            {label}
            {required && <span className="text-destructive ms-1">*</span>}
          </Label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'justify-between font-normal h-auto min-h-10',
                fullWidth && 'w-full',
                size === 'sm' && 'min-h-8 text-sm',
                selectedRegions.length === 0 && 'text-muted-foreground',
                error && 'border-destructive'
              )}
            >
              <div className="flex flex-wrap gap-1 py-1">
                {selectedRegions.length === 0 ? (
                  placeholder || t('pickers.selectRegions')
                ) : (
                  <>
                    {displayedRegions.map((region) => (
                      <Badge
                        key={region.code}
                        variant="secondary"
                        className="gap-1 pe-1 cursor-default"
                      >
                        {region.countries.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 cursor-help">
                                <Globe className="h-3 w-3" />
                                <span>{region.name}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="p-2">
                              <RegionCountriesTooltip region={region} />
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            <span>{region.name}</span>
                          </span>
                        )}
                        <button
                          type="button"
                          className="ms-1 rounded-full p-0.5 hover:bg-muted"
                          onClick={(e) => handleRemove(region.code, e)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {remainingCount > 0 && (
                      <Badge variant="outline" className="text-muted-foreground">
                        +{remainingCount}
                      </Badge>
                    )}
                  </>
                )}
              </div>
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchRegion')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noRegionFound')}</CommandEmpty>

              {/* Business Regions - Most commonly used */}
              {businessRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.business', 'Business Regions')}>
                  {businessRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Macro Regions (Continents) */}
              {macroRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.continents', 'Continents')}>
                  {macroRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Sub-Regions */}
              {subRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.subRegions', 'Sub-Regions')}>
                  {subRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Intermediate Regions */}
              {intermediateRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.intermediateRegions', 'Intermediate Regions')}>
                  {intermediateRegions.map(renderRegionItem)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
      </div>
    </TooltipProvider>
  );
}

export function getRegionByCode(code: string): RegionOption | undefined {
  return regionOptions.find((r) => r.code === code);
}

export function getRegionName(code: string): string {
  const region = getRegionByCode(code);
  return region?.name || code;
}

export function getRegionCountries(code: string): string[] {
  const region = getRegionByCode(code);
  return region?.countries || [];
}

export function getAllRegions(): RegionOption[] {
  return regionOptions;
}

export type { MultiRegionPickerProps };
